import { Text } from '@shared/components';
import { ActionExecutionEventType } from '@shared/generated/graphql';
import { useActionDefinitionPreview } from '@shared/graphql/hooks/actionDefinitions/useActionDefinitionPreview';
import { ActionExecutionEvent } from '@shared/types/action-execution';
import { formatSleepDescription } from 'clerk_common/stringification/sleep';
import {
  describeToolCall,
  getSkillToolCallId,
  SKILL_TOOL_ACTIONS,
  ToolCallKind,
  ToolCallParts,
  toToolCallVerb,
} from 'clerk_common/stringification/toolCalls';
import { ReactNode } from 'react';
import { PiBookOpenText, PiWrench } from 'react-icons/pi';

type MemoryAction = 'Deleted' | 'Updated';

const MEMORY_ACTIONS: Record<string, MemoryAction> = {
  delete_memory: 'Deleted',
  update_memory: 'Updated',
};
const SLEEP_TOOL_NAME = 'sleep_tool';
const DETAILED_TOOL_NAMES = new Set([
  'create_memory',
  'create_skill',
  SLEEP_TOOL_NAME,
  ...Object.keys(MEMORY_ACTIONS),
  ...Object.keys(SKILL_TOOL_ACTIONS),
]);
const UNKNOWN_TOOL_GROUP_KEY = 'unknown-tool';
const TOOL_CALL_ICONS: Record<ToolCallKind, typeof PiWrench> = {
  skill: PiBookOpenText,
  tool: PiWrench,
};

// NOTE(wassim): Tools without a detailed label read the same however they were
// called, so they group on name alone; detailed ones need identical input and
// metadata.
export function getToolCallGroupKey(event: ActionExecutionEvent): string {
  if (event.data?.eventDataType !== ActionExecutionEventType.TOOL_USED) {
    return UNKNOWN_TOOL_GROUP_KEY;
  }
  const { metadata, toolInput, toolName } = event.data;
  return DETAILED_TOOL_NAMES.has(toolName)
    ? `${toolName}:${toStableJson([toolInput, metadata ?? null])}`
    : toolName;
}

function toStableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(toStableJson).join(',')}]`;
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const entries = Object.keys(record)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${toStableJson(record[key])}`);
    return `{${entries.join(',')}}`;
  }
  return JSON.stringify(value) ?? 'undefined';
}

export function UserToolCallEvent({
  event,
  isRunning,
  repeatCount = 1,
}: {
  event: ActionExecutionEvent;
  isRunning: boolean;
  repeatCount?: number;
}) {
  return (
    <div className="flex items-baseline gap-1.5">
      <ToolCallDescription event={event} isRunning={isRunning} />
      {repeatCount > 1 && <RepeatCount count={repeatCount} />}
    </div>
  );
}

function ToolCallDescription({
  event,
  isRunning,
}: {
  event: ActionExecutionEvent;
  isRunning: boolean;
}) {
  if (event.data?.eventDataType !== ActionExecutionEventType.TOOL_USED) {
    return <GenericToolCall isRunning={isRunning} />;
  }

  const { metadata, toolInput, toolName } = event.data;
  if (toolName === 'create_skill') {
    return (
      <InputToolCall
        input={toolInput}
        inputKey="name"
        isRunning={isRunning}
        subject="skill"
        toolName={toolName}
      />
    );
  }
  if (toolName === 'create_memory') {
    return (
      <InputToolCall
        input={toolInput}
        inputKey="comment"
        isRunning={isRunning}
        subject="memory"
        toolName={toolName}
      />
    );
  }
  if (SKILL_TOOL_ACTIONS[toolName]) {
    return (
      <SkillToolCall
        input={toolInput}
        isRunning={isRunning}
        toolName={toolName}
      />
    );
  }
  if (MEMORY_ACTIONS[toolName]) {
    return (
      <MemoryToolCall
        action={MEMORY_ACTIONS[toolName]}
        input={toolInput}
        isRunning={isRunning}
        previousComment={metadata?.previousComment}
        toolName={toolName}
      />
    );
  }
  if (toolName === SLEEP_TOOL_NAME) {
    const description = formatSleepDescription(toolInput);
    return description ? (
      <ToolCallText>{description}</ToolCallText>
    ) : (
      <GenericToolCall isRunning={isRunning} toolName={toolName} />
    );
  }
  return <GenericToolCall isRunning={isRunning} toolName={toolName} />;
}

function GenericToolCall({
  isRunning,
  toolName,
}: {
  isRunning: boolean;
  toolName?: string;
}) {
  return (
    <ToolCallLabel
      parts={
        toolName
          ? describeToolCall({ toolName })
          : { kind: 'tool', verb: 'Used', name: 'Unknown tool' }
      }
      isRunning={isRunning}
    />
  );
}

function InputToolCall({
  input,
  inputKey,
  isRunning,
  subject,
  toolName,
}: {
  input: object;
  inputKey: string;
  isRunning: boolean;
  subject: string;
  toolName: string;
}) {
  const value = getStringInput(input, inputKey);
  return value ? (
    <ToolCallText>
      {toToolCallVerb('Created', isRunning)} {subject} - {value}
    </ToolCallText>
  ) : (
    <GenericToolCall isRunning={isRunning} toolName={toolName} />
  );
}

function SkillToolCall({
  input,
  isRunning,
  toolName,
}: {
  input: object;
  isRunning: boolean;
  toolName: string;
}) {
  const skillId = getSkillToolCallId(input);
  if (!skillId) {
    return <GenericToolCall isRunning={isRunning} toolName={toolName} />;
  }
  return (
    <ResolvedSkillToolCall
      isRunning={isRunning}
      skillId={skillId}
      toolName={toolName}
    />
  );
}

function ResolvedSkillToolCall({
  isRunning,
  skillId,
  toolName,
}: {
  isRunning: boolean;
  skillId: string;
  toolName: string;
}) {
  const { actionDefinition } = useActionDefinitionPreview({ id: skillId });
  return (
    <ToolCallLabel
      parts={describeToolCall({
        toolName,
        skillName: actionDefinition?.name,
      })}
      isRunning={isRunning}
    />
  );
}

function MemoryToolCall({
  action,
  input,
  isRunning,
  previousComment,
  toolName,
}: {
  action: MemoryAction;
  input: object;
  isRunning: boolean;
  previousComment?: string | null;
  toolName: string;
}) {
  const updatedComment = getStringInput(input, 'comment');
  if (!previousComment || (action === 'Updated' && !updatedComment)) {
    return <GenericToolCall isRunning={isRunning} toolName={toolName} />;
  }
  return (
    <ToolCallText>
      {action} memory - {previousComment}
      {updatedComment ? ` -> ${updatedComment}` : ''}
    </ToolCallText>
  );
}

function RepeatCount({ count }: { count: number }) {
  return (
    <>
      <span aria-hidden className="flex shrink-0">
        <Text
          as="span"
          type="body-xs"
          className="rounded-full bg-gray-100 px-1.5 py-0.5 tabular-nums leading-none text-gray-500"
        >
          {`×${count}`}
        </Text>
      </span>
      <span className="sr-only">{`called ${count} times`}</span>
    </>
  );
}

function ToolCallLabel({
  parts: { kind, verb, name },
  isRunning,
}: {
  parts: ToolCallParts;
  isRunning: boolean;
}) {
  const Icon = TOOL_CALL_ICONS[kind];
  const hasVerb = isRunning || verb !== 'Used';
  return (
    <span className="flex items-center gap-1.5">
      <Icon
        aria-label={kind}
        className="shrink-0 text-gray-400"
        role="img"
        size={12}
      />
      <ToolCallText>
        {hasVerb ? `${toToolCallVerb(verb, isRunning)} ${name}` : name}
      </ToolCallText>
    </span>
  );
}

function ToolCallText({ children }: { children: ReactNode }) {
  return (
    <Text type="body-sm" className="italic text-gray-500">
      {children}
    </Text>
  );
}

function getStringInput(input: object, key: string): string | undefined {
  const value = (input as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : undefined;
}
